import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { identity } from '@repletoryreach/sdk-api';
import { TenantName } from '../../../../components/TenantName';
import { formatDateTime, formatInteger, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * One key, and where it came from.
 *
 * The list answers "what keys exist". An operator who finds an unfamiliar key
 * live on a customer's account has two other questions, and neither had an
 * answer anywhere before this: who made it and from what address, and what has
 * actually been using it. The last two figures come from the usage log rather
 * than from columns on the key, so `use_count` is calls the log still holds and
 * not a lifetime total, which is why the panel says so.
 */
export function KeyDetailDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const t = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'api-keys', 'detail', id],
    queryFn: () => identity.adminGetApiKey(id!),
    enabled: id !== null
  });

  const k = data?.data;

  return (
    <Drawer opened={id !== null} onClose={onClose} title={t('settings.apiKeys.detail.title')} position="right" size="lg">
      {isLoading && <Text role="bodySmall" c={MUTED}>{t('common.loading')}</Text>}
      {error && <Alert color="error" title={t('settings.apiKeys.detail.loadError')}>{(error as Error).message}</Alert>}
      {k && (
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Stack gap={2}>
              <Text fw={600}>{k.name}</Text>
              <Text role="caption" c={MUTED}>{k.key_prefix}…</Text>
            </Stack>
            <StatusBadge status={k.is_active ? 'healthy' : 'inactive'}>
              {k.is_active ? t('status.active') : t('settings.apiKeys.status.revoked')}
            </StatusBadge>
          </Group>

          <Stack gap="xs">
            <Text role="bodySmall" fw={600}>{t('settings.apiKeys.detail.whose')}</Text>
            <Row label={t('settings.apiKeys.columns.tenant')} value={<TenantName id={k.organisation_id} />} />
            <Row
              label={t('settings.apiKeys.detail.scopes')}
              value={(k.scopes ?? []).length === 0
                ? <Text role="caption" c={MUTED}>{t('settings.apiKeys.detail.noScopes')}</Text>
                : <Group gap={4} wrap="wrap">{k.scopes.map((s) => <Badge key={s} size="sm" variant="light">{s}</Badge>)}</Group>}
            />
            <Row label={t('settings.apiKeys.detail.rateLimit')} value={t('settings.apiKeys.detail.perMinute', { n: formatInteger(k.rate_limit_per_minute) })} />
          </Stack>

          <Stack gap="xs">
            <Text role="bodySmall" fw={600}>{t('settings.apiKeys.detail.provenance')}</Text>
            <Row label={t('settings.apiKeys.detail.createdAt')} value={formatDateTime(k.created_at)} />
            <Row
              label={t('settings.apiKeys.detail.createdBy')}
              value={k.created_by_email ?? <Text role="caption" c={MUTED}>{t('settings.apiKeys.detail.unrecorded')}</Text>}
            />
            <Row
              label={t('settings.apiKeys.detail.createdIp')}
              value={k.created_ip ?? <Text role="caption" c={MUTED}>{t('settings.apiKeys.detail.unrecorded')}</Text>}
            />
            <Row
              label={t('settings.apiKeys.detail.expiresAt')}
              value={k.expires_at ? formatDateTime(k.expires_at) : <Text role="caption" c={MUTED}>{t('settings.apiKeys.detail.noExpiry')}</Text>}
            />
          </Stack>

          <Stack gap="xs">
            <Text role="bodySmall" fw={600}>{t('settings.apiKeys.detail.use')}</Text>
            <Row
              label={t('settings.apiKeys.columns.lastUsed')}
              value={k.last_used_at
                ? <span title={formatDateTime(k.last_used_at)}>{formatRelative(k.last_used_at)}</span>
                : <Text role="caption" c={MUTED}>{t('settings.apiKeys.neverUsed')}</Text>}
            />
            <Row
              label={t('settings.apiKeys.detail.lastUsedIp')}
              value={k.last_used_ip ?? <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>}
            />
            <Row label={t('settings.apiKeys.detail.useCount')} value={formatInteger(k.use_count)} />
            {/* The log is the source, so the figure is what the log still
                holds. Presenting it as a lifetime total would be a claim about
                data that may have been trimmed. */}
            <Text role="caption" c={MUTED}>{t('settings.apiKeys.detail.useCountNote')}</Text>
          </Stack>

          {k.revoked_at && (
            <Alert color="warning" title={t('settings.apiKeys.detail.revoked')}>
              <Stack gap={2}>
                <Text role="bodySmall">{formatDateTime(k.revoked_at)}</Text>
                {k.revoked_by_email && (
                  <Text role="bodySmall">{t('settings.apiKeys.detail.revokedBy', { who: k.revoked_by_email })}</Text>
                )}
                <Text role="bodySmall">
                  {k.revoked_reason ?? t('settings.apiKeys.detail.noReason')}
                </Text>
              </Stack>
            </Alert>
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function Row({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
      <Text role="caption" c={MUTED}>{label}</Text>
      <div style={{ textAlign: 'right', wordBreak: 'break-word' }}>
        {typeof value === 'string' ? <Text role="bodySmall">{value}</Text> : value}
      </div>
    </Group>
  );
}
